import { PropsWithChildren, useEffect, useRef, useState } from 'react';

import { Box, BoxProps, Typography, TypographyProps } from '@mui/material';

import { useElementWidth } from '@cast/utils';

import { Tooltip, TooltipProps } from './Tooltip';
import { mergeSx } from '../../utils';

type Props = PropsWithChildren<
  {
    TooltipProps?: Partial<TooltipProps>;
    TypographyProps?: TypographyProps<'p', { component?: 'p' }>;
  } & BoxProps
>;

export const EllipsisWithTooltip = ({
  children,
  TooltipProps = { arrow: true, placement: 'top' },
  TypographyProps = {},
  sx,
  ...boxProps
}: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  /**
   * This property handles container resizing (e.g., for column headers).
   * Crucially, it also implicitly resolves a specific font loading issue.
   * If this logic is altered or removed, ensure font rendering don't affect logic.
   * Specifically, check table header cells with an ellipsis across multiple tables to confirm no random display glitches occur.
   */
  const refWidth = useElementWidth(ref.current);

  useEffect(() => {
    if (ref.current) {
      const textElement = ref.current.firstElementChild as HTMLElement;
      const isEllipsis = textElement.offsetWidth < textElement.scrollWidth;
      if (isTooltipVisible !== isEllipsis) {
        setIsTooltipVisible(isEllipsis);
      }
    }
    // eslint-disable-next-line
  }, [!!ref.current, refWidth]);

  return (
    <Box
      sx={mergeSx(
        {
          display: 'flex',
          maxWidth: '100%',
          minWidth: 0,
          typography: 'inherit',
        },
        sx
      )}
      {...boxProps}
      ref={ref}
    >
      {/*
       * Always render Tooltip (control via disableHoverListener instead of conditional mount).
       * Conditionally mounting/unmounting Tooltip changes DOM structure, which changes element
       * dimensions, which triggers ResizeObserver -> useEffect -> state flip -> infinite loop
       * when text is near the ellipsis boundary.
       */}
      <Tooltip
        title={children ?? ''}
        {...TooltipProps}
        disableHoverListener={!isTooltipVisible}
        disableFocusListener={!isTooltipVisible}
        disableTouchListener={!isTooltipVisible}
      >
        <Typography
          variant="inherit"
          {...TypographyProps}
          sx={{
            ...TypographyProps.sx,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: isTooltipVisible ? 'ellipsis' : undefined,
          }}
          component="p"
        >
          {children}
        </Typography>
      </Tooltip>
    </Box>
  );
};
